import { Application, Assets, Container, Text } from 'pixi.js';
import { AudioManager } from './AudioManager';
import { InputManager } from './InputManager';
import { ScreenManager, type Screen, type ScreenFactory } from './ScreenManager';
import { loadSfxMap, loadSongs } from './AssetManager';
import { withBase } from './paths';
import { infoPanel } from '../ui/InfoPanel';
import type { SongEntry } from '../data/types';

export const PANEL_SIZE = 160;
export const PANEL_GAPS = [4, 5, 4] as const; // gaps between columns / rows
/** Logical container size: 4*PANEL_SIZE + sum(PANEL_GAPS). The grid is 1:1. */
export const LOGICAL_SIZE = PANEL_SIZE * 4 + PANEL_GAPS[0] + PANEL_GAPS[1] + PANEL_GAPS[2]; // 653

/** Returns x offset (in logical units) of the column index 0..3. */
export function colX(col: number): number {
  let x = 0;
  for (let i = 0; i < col; i++) x += PANEL_SIZE + PANEL_GAPS[i];
  return x;
}
export function rowY(row: number): number {
  let y = 0;
  for (let i = 0; i < row; i++) y += PANEL_SIZE + PANEL_GAPS[i];
  return y;
}
export function cellRect(cell: number) {
  const col = cell % 4;
  const row = (cell / 4) | 0;
  return { x: colX(col), y: rowY(row), w: PANEL_SIZE, h: PANEL_SIZE };
}

export interface GameContext {
  app: Application;
  root: Container;          // already scaled to current viewport; child of stage
  audio: AudioManager;
  input: InputManager;
  songs: SongEntry[];
  sfx: Record<string, string | null>;
  /** Convert a clientX/clientY (CSS pixels) -> grid cell index 0..15, or -1. */
  hitTestCell(clientX: number, clientY: number): number;
  switchScreen(factory: ScreenFactory): void;
  /** Play a named SFX (id from sfx.json). Lazy decoded. */
  playSfx(id: string, opts?: { volume?: number }): void;
  /** Current logical viewport square size (CSS px). */
  logicalScale: () => number;
  /** Top-left offset of the playfield square within the canvas, in CSS px.
   *  Local-multiplayer needs this to draw OUTSIDE the centered square. */
  rootOffset: () => { x: number; y: number };
  /** Canvas size in CSS pixels (the viewport, not the playfield square). */
  canvasSize: () => { w: number; h: number };
  /** Translate a logical cell index 0..15 into a CSS-pixel rect on screen. */
  cellScreenRect: (cell: number) => { x: number; y: number; w: number; h: number };
  /** Map a clientX/clientY (CSS pixels) into logical playfield coords; returns null when
   *  the point falls outside the LOGICAL_SIZE × LOGICAL_SIZE play area. */
  clientToLogical: (clientX: number, clientY: number) => { x: number; y: number } | null;
}

let _ctx: GameContext | null = null;
export function getGameContext(): GameContext {
  if (!_ctx) throw new Error('GameContext not initialized');
  return _ctx;
}

export class Game {
  private app!: Application;
  private root!: Container;
  private audio: AudioManager;
  private input: InputManager;
  private screens!: ScreenManager;
  private host: HTMLElement;
  private songs: SongEntry[] = [];
  private sfx: Record<string, string | null> = {};
  private scale = 1;
  private offsetX = 0;
  private offsetY = 0;

  constructor(host: HTMLElement) {
    this.host = host;
    this.audio = new AudioManager();
    this.input = new InputManager();
  }

  async start() {
    this.app = new Application();
    await this.app.init({
      resizeTo: window,
      backgroundColor: 0x000000,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(2, window.devicePixelRatio || 1),
      eventMode: 'none', // we handle pointers on the canvas directly
    });
    this.host.appendChild(this.app.canvas);
    // Cap rendering to 30 FPS for authentic jubeat look. Input remains event-driven (ms precision).
    this.app.ticker.maxFPS = 30;

    // Root container is the 782x782 logical area, scaled to fit min(width, height).
    this.root = new Container();
    this.app.stage.addChild(this.root);

    // Show a loading text while we fetch
    const loading = new Text({ text: 'Loading…', style: { fill: 0xffffff, fontSize: 28 } });
    loading.x = 16; loading.y = 16;
    this.app.stage.addChild(loading);

    // Load data + first interaction unlock
    const [songs, sfx] = await Promise.all([loadSongs(), loadSfxMap()]);
    this.songs = songs;
    this.sfx = sfx;
    this.app.stage.removeChild(loading);
    loading.destroy();

    this.screens = new ScreenManager(this.root);
    this.applyLayout();

    _ctx = {
      app: this.app,
      root: this.root,
      audio: this.audio,
      input: this.input,
      songs: this.songs,
      sfx: this.sfx,
      hitTestCell: (cx, cy) => this.hitTestCell(cx, cy),
      switchScreen: (f) => { void this.screens.switch(f); },
      playSfx: (id, opts) => this.playNamedSfx(id, opts),
      logicalScale: () => this.scale,
      rootOffset: () => ({ x: this.offsetX, y: this.offsetY }),
      canvasSize: () => ({ w: window.innerWidth, h: window.innerHeight }),
      cellScreenRect: (cell) => {
        const r = cellRect(cell);
        const rect = this.app.canvas.getBoundingClientRect();
        return {
          x: rect.left + this.offsetX + r.x * this.scale,
          y: rect.top + this.offsetY + r.y * this.scale,
          w: r.w * this.scale,
          h: r.h * this.scale,
        };
      },
      clientToLogical: (cx, cy) => {
        const rect = this.app.canvas.getBoundingClientRect();
        const lx = (cx - rect.left - this.offsetX) / this.scale;
        const ly = (cy - rect.top - this.offsetY) / this.scale;
        if (lx < 0 || ly < 0 || lx >= LOGICAL_SIZE || ly >= LOGICAL_SIZE) return null;
        return { x: lx, y: ly };
      },
    };

    this.input.attach(this.app.canvas, (cx, cy) => this.hitTestCell(cx, cy));
    window.addEventListener('resize', this.applyLayout);
    window.addEventListener('orientationchange', this.applyLayout);

    // Pause all audio when the page loses focus / goes hidden. Suspending the AudioContext
    // freezes ctx.currentTime too, so musicTime() stays consistent and notes don't drift.
    const onBlur = () => { void this.audio.ctx.suspend(); };
    const onFocus = () => { void this.audio.ctx.resume(); };
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) onBlur(); else onFocus();
    });
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    // Per-frame update
    this.app.ticker.add(() => {
      const dt = this.app.ticker.deltaMS / 1000;
      this.screens.update(dt, performance.now());
    });

    // Audio unlock is gated on the consent modal in index.html — clicking 허용 is the
    // user gesture that resumes the AudioContext, which in turn makes the idle BGM audible.
    // In a PWA standalone launch the OS already counts the app open as a user gesture
    // and browsers typically allow AudioContext.resume() without an in-page tap, so we
    // try to unlock straight away and only fall back to the modal if that didn't take.
    const consent = document.getElementById('audio-consent');
    const consentBtn = document.getElementById('audio-consent-btn');
    const isStandalonePwa =
      window.matchMedia?.('(display-mode: standalone)').matches
      || window.matchMedia?.('(display-mode: fullscreen)').matches
      || (navigator as any).standalone === true;
    if (consent && consentBtn) {
      const dismissModal = () => {
        consent.classList.add('is-hidden');
        consent.remove();
      };
      const accept = async () => {
        consentBtn.removeEventListener('click', accept);
        await this.audio.unlock();
        dismissModal();
      };
      consentBtn.addEventListener('click', accept);
      if (isStandalonePwa) {
        void this.audio.unlock().then(() => {
          if (this.audio.ctx.state === 'running') dismissModal();
        }).catch(() => { /* keep modal up as fallback */ });
      }
    } else {
      // Fallback if the modal markup is missing — degrade to first-gesture unlock.
      const unlock = async () => {
        await this.audio.unlock();
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
      };
      window.addEventListener('pointerdown', unlock, { once: true, passive: true });
      window.addEventListener('keydown', unlock, { once: true });
    }

    // Fire-and-forget asset preloading (textures + SFX). Runs in the background while
    // the first screen is rendering so subsequent navigation is instant.
    void this.preloadCommonAssets();

    // Register the ambient idle BGM. AudioManager auto-plays it whenever no real
    // music is active, and silences it as soon as a song/preview starts.
    const idleUrl = this.sfx['idle_bgm'];
    if (idleUrl) {
      this.audio.load(idleUrl)
        .then((buf) => this.audio.setIdleBgm(buf))
        .catch((e) => console.warn('idle_bgm load fail', e));
    }

    // Dev: `?mock=<id>` jumps directly to a screen with mock data.
    const mockId = new URLSearchParams(location.search).get('mock');
    if (mockId) {
      const { enterMockScreen } = await import('../dev/mocks');
      await enterMockScreen(mockId, { switchScreen: (f) => { void this.screens.switch(f); } });
      return;
    }

    // Skip title — go directly to song select.
    const { SongSelectScreen } = await import('../screens/SongSelectScreen');
    await this.screens.switch(() => new SongSelectScreen());
  }

  private async preloadCommonAssets() {
    const SMM = withBase('/dataset/textures/tex_l44av_smc_mm');
    const TEXT_KO = withBase('/dataset/tex_l44av_smc_mm_text_ko');
    const textureUrls: string[] = [];

    // CD discs + rotating effect overlays
    for (const s of ['00', '10', '20']) textureUrls.push(`${SMM}/SMM_JKCR_${s}.webp`);
    for (let i = 0; i < 4; i++) textureUrls.push(`${SMM}/SMM_JKCR_EFF_${i}.webp`);

    // Level badge backgrounds + number variants
    for (const s of ['00', '10', '20']) textureUrls.push(`${SMM}/SMM_LVBD_${s}.webp`);
    for (let i = 1; i <= 10; i++) textureUrls.push(`${SMM}/SMM_LV_${i}.webp`);
    for (let i = 0; i <= 9; i++) textureUrls.push(`${SMM}/SMM_LV_9_${i}.webp`);
    for (let i = 0; i <= 9; i++) textureUrls.push(`${SMM}/SMM_LV_10_${i}.webp`);

    // Close-button parts (sort menu)
    textureUrls.push(`${SMM}/SMM_TR_BD_2.webp`, `${SMM}/SMM_TR_3.webp`, `${SMM}/SMM_TR_1.webp`);

    // Sort-menu top-category text images
    for (const f of ['SMM_T0200_KO.webp', 'SMM_T0300_KO.webp', 'SMM_T0400_KO.webp', 'SMM_T0420_KO.webp', 'SMM_T0500_KO.webp', 'SMM_T0860_KO.webp']) {
      textureUrls.push(`${TEXT_KO}/${f}`);
    }
    // Sub-category text images
    // NAME (0312-0319), LEVEL (0410-0419), VERSION (0430-0441), GENRE (0512-0520)
    const pushRange = (lo: number, hi: number) => {
      for (let i = lo; i <= hi; i++) {
        textureUrls.push(`${TEXT_KO}/SMM_T${String(i).padStart(4, '0')}_KO.webp`);
      }
    };
    pushRange(312, 319);
    pushRange(410, 419);
    pushRange(430, 441);
    pushRange(512, 520);

    // Menu cell backgrounds (BGD_BASE2_01..04 + EFF_APE)
    for (const id of ['01', '02', '03', '04']) {
      textureUrls.push(withBase(`/dataset/textures/tex_l44avby_background/BGD_BASE2_${id}.webp`));
    }
    textureUrls.push(withBase('/dataset/textures/tex_l44avby_background/BGD_BASE_EFF_APE.webp'));

    // In-game combo display: COMBO label + digit textures (regular + full-combo variants).
    const PLAY_BG = withBase('/dataset/tex_l44av_play_background');
    textureUrls.push(`${PLAY_BG}/PLB_CMB.webp`);
    for (let d = 0; d <= 9; d++) {
      textureUrls.push(`${PLAY_BG}/PLB_CMB_${d}.webp`);
      textureUrls.push(`${PLAY_BG}/PLB_CMB_9${d}.webp`);
    }

    for (const url of textureUrls) Assets.load(url).catch(() => {});

    // Decode every mapped SFX once so first play has no decode latency
    for (const url of Object.values(this.sfx)) {
      if (url) this.audio.load(url).catch(() => {});
    }
  }

  private applyLayout = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const s = Math.min(w, h) / LOGICAL_SIZE;
    this.scale = s;
    this.offsetX = (w - LOGICAL_SIZE * s) / 2;
    // Portrait: pin the playfield to the bottom of the screen so the buttons fall under the user's
    // thumb instead of floating in the vertical center. Landscape (desktop) keeps centered.
    this.offsetY = h > w ? (h - LOGICAL_SIZE * s) : (h - LOGICAL_SIZE * s) / 2;
    this.root.scale.set(s);
    this.root.position.set(this.offsetX, this.offsetY);
    this.screens?.setSize(LOGICAL_SIZE, LOGICAL_SIZE);
    infoPanel().setLayout({ playfieldHeightPx: LOGICAL_SIZE * s, portrait: h > w });
  };

  private hitTestCell(clientX: number, clientY: number): number {
    const rect = this.app.canvas.getBoundingClientRect();
    const localX = clientX - rect.left - this.offsetX;
    const localY = clientY - rect.top - this.offsetY;
    const lx = localX / this.scale;
    const ly = localY / this.scale;
    if (lx < 0 || ly < 0 || lx >= LOGICAL_SIZE || ly >= LOGICAL_SIZE) return -1;
    // Find column
    let col = -1, accX = 0;
    for (let c = 0; c < 4; c++) {
      if (lx >= accX && lx < accX + PANEL_SIZE) { col = c; break; }
      accX += PANEL_SIZE + (PANEL_GAPS[c] ?? 0);
    }
    let row = -1, accY = 0;
    for (let r = 0; r < 4; r++) {
      if (ly >= accY && ly < accY + PANEL_SIZE) { row = r; break; }
      accY += PANEL_SIZE + (PANEL_GAPS[r] ?? 0);
    }
    if (col < 0 || row < 0) return -1;
    return row * 4 + col;
  }

  private playNamedSfx(id: string, opts?: { volume?: number }) {
    // Fall back to ui_default when the requested SFX is missing/unmapped.
    const url = this.sfx[id] ?? this.sfx['ui_default'];
    if (!url) return;
    void this.audio.playSfx(url, opts);
  }
}
